/**
 * The quantities a part states, edited in place: a TABLE, one row per quantity.
 *
 * Patrick's fourth and sixth observations together - the detail pane should be
 * editable, and a table reads better than a text box. It is a FIXED LIST of the
 * quantities the model declares rather than free-form rows: which ones there are
 * comes from `attributes` on the payload, the model's own statement via
 * `@EchelonSysML::RollsUp`, so a fourth quantity added to the profile appears here
 * with no change on this side and nobody can invent a row for a quantity nothing
 * rolls up.
 *
 * The control is `FieldControl`, the same one the generated forms use, and that is
 * deliberate: a quantity is the value BEST got wrong before - writing it as a
 * quoted string, `:>> dryMass = "3.5 [SI::kg]";`, which re-parses cleanly and is
 * wrong only in the exported text - so a second control here would be a second set
 * of rules about the value most likely to be quietly wrong. It takes a NUMBER and
 * shows the unit from the descriptor, because the library declares
 * `dryMass : ISQ::MassValue` and the unit is a property of the field rather than a
 * choice the typist makes.
 *
 * Every edit is gated on `useCanWrite()`, DISABLED with the reason rather than
 * hidden: a missing control is indistinguishable from a broken page.
 *
 * A usage gets no editor at all, and is told why. The rollup refuses a quantity
 * stated on a usage - "state it on the definition, or specialize the definition" -
 * so a box here would offer a value that the totals then decline to count.
 */

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import HelpTip from '../common/HelpTip'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import FieldControl from '../entity/FieldControl'
import type { FieldSchema, KindSchema, Schema } from '../../schema'
import { MONOSPACE } from '../../theme'
import {
  ATTRIBUTE_TABLE_LABEL,
  USAGE_STATES_NOTHING,
  attributeRefusal,
} from './parts-editing'
import type { AttributeDraft } from './parts-editing'
import type { WritePermission } from './parts-model'

/**
 * The registry field behind one declared quantity.
 *
 * Matched by NAME, because the descriptor and the registry field are two views of
 * the same column - `dry_mass` in both. A quantity the registry does not carry
 * still gets a control, built from the descriptor: the alternative is a row the
 * model declares and this screen silently omits.
 */
export function fieldFor(kind: KindSchema, draft: AttributeDraft): FieldSchema {
  const found = kind.fields.find((field) => field.name === draft.name)
  if (found) return found
  return {
    name: draft.name,
    kind: 'quantity',
    // The draft knows the feature the descriptor binds, so the synthesised
    // field carries it rather than claiming the attribute binds nothing.
    feature: draft.feature,
    label: draft.label,
    help: '',
    is_list: false,
    unit: draft.unit,
    derived: false,
    enum_type: '',
    base_type: '',
    target: '',
  }
}

export default function AttributesTable({
  schema,
  kind,
  drafts,
  isDefinition,
  permission,
  onChange,
  onSave,
  onRevert,
  dirty,
  saving,
  failure = '',
  fieldErrors = {},
}: {
  schema: Schema
  kind: KindSchema
  drafts: AttributeDraft[]
  isDefinition: boolean
  permission: WritePermission
  onChange: (name: string, value: unknown) => void
  onSave: () => void
  onRevert: () => void
  /** Whether anything has been edited, so Save says something true. */
  dirty: boolean
  saving: boolean
  /** What the API refused, in its own words. */
  failure?: string
  /** Per-field complaints from the API, keyed by field name. */
  fieldErrors?: Record<string, string>
}) {
  if (drafts.length === 0) return null

  if (!isDefinition) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '60ch' }}>
        {USAGE_STATES_NOTHING}
      </Typography>
    )
  }

  const refusal = attributeRefusal(drafts, permission)
  const blocked = !permission.allowed

  return (
    <Stack spacing={1}>
      {failure && <Alert severity="error">{failure}</Alert>}

      <Table size="small" aria-label={ATTRIBUTE_TABLE_LABEL}>
        <TableHead>
          <TableRow>
            {/* ATTRIBUTE, not Quantity. The sections above are Part
                Attributes and Rollup Attributes, and the column naming
                the same thing a third way made them read as three
                different things. Several of these are not quantities
                anyway - a subsystem is an enumeration. */}
            <TableCell>Attribute</TableCell>
            <TableCell>Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {drafts.map((draft) => (
            <TableRow key={draft.name}>
              <TableCell sx={{ width: '12rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {draft.label}
                  {/* The attribute's own description, from the profile, behind a
                      '?'. It printed as helper text under every control, which
                      put a paragraph on each of ten rows - so the pane was three
                      screens of prose about attributes mostly left unset. */}
                  {fieldFor(kind, draft).help && (
                    <HelpTip
                      title={fieldFor(kind, draft).help}
                      label={`What ${draft.label} means`}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontFamily: MONOSPACE }}
                >
                  {draft.feature || draft.name}
                </Typography>
              </TableCell>
              <TableCell>
                {/* Disabled with the reason on it, never hidden - and the tooltip
                    is on a span, because a disabled control fires no events. */}
                <Tooltip title={blocked ? permission.reason : ''} describeChild>
                  <span>
                    <FieldControl
                      schema={schema}
                      kind={kind}
                      // Label and help are the row's, not the control's: the
                      // name is the cell to the left and the description is its
                      // '?'. ariaLabel keeps the input NAMED - a blank label
                      // without it leaves a box a screen reader calls "edit
                      // text". An error still shows, which is the one piece of
                      // helper text that must never be hidden.
                      field={{ ...fieldFor(kind, draft), label: '', help: '' }}
                      ariaLabel={draft.label}
                      value={draft.value}
                      onChange={(next) => onChange(draft.name, next)}
                      disabled={blocked || saving}
                      error={fieldErrors[draft.name]}
                    />
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Tooltip title={refusal} describeChild>
          <span>
            <Button
              size="small"
              variant="contained"
              onClick={onSave}
              disabled={Boolean(refusal) || !dirty || saving}
              startIcon={saving ? <CircularProgress size={14} /> : undefined}
            >
              {saving ? 'Saving…' : 'Save attributes'}
            </Button>
          </span>
        </Tooltip>
        <Button size="small" onClick={onRevert} disabled={!dirty || saving}>
          Discard
        </Button>
        <Box sx={{ flexGrow: 1 }} />
      </Stack>

      {/* ATTRIBUTE_NOTE and CLEARING_NOTE used to print here, two paragraphs of
          caption under every part. They are on the section's own '?' now - see
          DecompositionPane. Both are worth reading once and neither is worth
          reading on every part somebody clicks. */}
    </Stack>
  )
}
